// src/pages/SurveyDashboard.tsx — Customer Experience Survey Dashboard
//
// Native React replica of the "CRM - Customer Experience Survey" report, in the
// portal's design system (see DASHBOARD_BUILD_RULES.md). Governed: dashboards:customer_survey.
// Two tabs:
//   Overview          — KPIs, CSAT / Times-Contacted / Resolved charts, Summary/Department
//                       toggle, and a survey detail table (CRM link, reference, dates, org).
//   Executive Summary — period-over-period KPIs, satisfaction breakdown, department
//                       satisfied/dissatisfied split, and a satisfied/dissatisfied word cloud.
//
// Data: /api/survey/* (Oracle Service Cloud Post-Resolution Survey via dw_gold).
// Score 0/NULL = "Not Applicable", excluded from averages. Directorate / department /
// service + the detail table enrich via the request join (warehouse RequestKey fix);
// survey-intrinsic KPIs work regardless.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, Dropdown, IDropdownOption,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import {
  CaretLeft, ArrowsClockwise, Smiley, Info, ArrowUp, ArrowDown,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const TEAL  = '#0D9488';
const GREEN = '#16a34a';
const RED   = '#DC2626';
const GREY  = '#747474';

// CSAT 5-band diverging colours (green → red)
const BAND_COLOURS: Record<string, string> = {
  'Very satisfied':                    '#15803d',
  'Satisfied':                         '#4ade80',
  'Neither satisfied nor dissatisfied':'#f59e0b',
  'Dissatisfied':                      '#f97316',
  'Very dissatisfied':                 '#dc2626',
};
const SAT_GREEN = '#16a34a';
const DISSAT_RED = '#dc2626';

// CRM deep link — opens the incident in the Boroondara Oracle Service Cloud agent
// console (keyed by incident id = Dim_Request.RequestId). Optional env override.
const CRM_AGENT_BASE = process.env.REACT_APP_CRM_AGENT_BASE || 'https://connect.boroondara.vic.gov.au/AgentWeb/';
function crmIncidentUrl(incidentId: string): string {
  return `${CRM_AGENT_BASE}?return_path=${encodeURIComponent(`/Bookmark/Incident/${incidentId}`)}#/Incident/${incidentId}`;
}

// ── Tabs ────────────────────────────────────────────────────────────────────────
type TabKey = 'overview' | 'executive';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview',  label: 'Overview'          },
  { key: 'executive', label: 'Executive Summary' },
];

// ── Types ─────────────────────────────────────────────────────────────────────
interface RespRow { response: string; count: number; pct: number | null }
interface DeptRow { department: string; satisfied: number; dissatisfied: number; total: number }
interface OverviewData {
  kpis: { surveysCompleted: number; totalRequests: number; completionRate: number | null;
    satisfied: number; dissatisfied: number; csatPct: number | null };
  csatDistribution: (RespRow & { score: number })[];
  timesContacted: RespRow[];
  requestResolved: RespRow[];
  byDepartment: DeptRow[];
  detail: { reference: string; incidentId: string | null; requestCloseDate: string; surveyDateKey: number | null;
    directorate: string; department: string; service: string;
    serviceTypeL1: string; serviceTypeL2: string }[];
  meta: { dateFrom: number; dateTo: number; generatedAt: string };
}
interface ExecKpis { satisfiedPct: number | null; dissatisfiedPct: number | null;
  contact3PlusPct: number | null; unresolvedPct: number | null }
interface ExecutiveData {
  kpis: { current: ExecKpis; previous: ExecKpis };
  csatDistribution: (RespRow & { score: number })[];
  timesContacted: RespRow[];
  requestResolved: RespRow[];
  byDepartment: DeptRow[];
  meta: { dateFrom: number; dateTo: number; prevFrom: number; prevTo: number; generatedAt: string };
}
interface WordCloudData { sentiment: string; words: { word: string; count: number }[]; sampled: number }
interface FilterOptions { directorates: string[]; allDepartments: string[];
  dirHierarchy: Record<string, string[]>; serviceGroups: string[] }

// ── Date helpers ──────────────────────────────────────────────────────────────
const DATE_RANGE_OPTIONS: IDropdownOption[] = [
  { key: '14d',  text: 'Last 14 days'   },
  { key: '30d',  text: 'Last 30 days'   },
  { key: '90d',  text: 'Last 3 months'  },
  { key: '365d', text: 'Last 12 months' },
  { key: 'all',  text: 'All time'       },
];
function buildDateKeys(preset: string): { from: number; to: number } {
  const today = new Date();
  const from = new Date(today);
  const days: Record<string, number> = { '14d': 14, '30d': 30, '90d': 90, '365d': 365 };
  if (preset in days) from.setDate(today.getDate() - days[preset]);
  else from.setFullYear(2025, 9, 1);  // all-time: earliest PRS data = Oct 2025
  const fmt = (d: Date) => parseInt(d.toISOString().slice(0, 10).replace(/-/g, ''), 10);
  return { from: fmt(from), to: fmt(today) };
}
function fmtNum(n: number): string { return n.toLocaleString('en-AU'); }
function trunc(s: string, max = 26): string { return s && s.length > max ? s.slice(0, max) + '…' : (s || ''); }
function fmtDateKey(k: number | null): string {
  if (!k) return '—';
  const s = String(k); const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${s.slice(6, 8)} ${m[parseInt(s.slice(4, 6), 10) - 1]} ${s.slice(0, 4)}`;
}
function fmtIso(d: string): string {
  if (!d) return '—';
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, mo, da] = d.split('-'); return da && mo && y ? `${da} ${m[+mo - 1]} ${y}` : d;
}

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles ────────────────────────────────────────────────────────────────────
const s = mergeStyleSets({
  page: { padding: 32, minHeight: 'calc(100vh - 52px)', background: '#F5F5F5',
    fontFamily: "'Segoe UI', system-ui, sans-serif", '@media (max-width:768px)': { padding: 16 } } as any,
  container: { maxWidth: 1320, margin: '0 auto' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, color: TEAL, fontWeight: 600,
    fontSize: 13, cursor: 'pointer', background: 'none', border: 'none', padding: 0, marginBottom: 4 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 18, flexWrap: 'wrap' as const, gap: 8 },
  pageTitle: { fontSize: 26, fontWeight: 800, color: '#1A1A1A', letterSpacing: '-0.02em',
    display: 'flex', alignItems: 'center', gap: 10 },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 6, fontSize: 13, fontWeight: 600,
    color: '#484848', cursor: 'pointer', selectors: { ':hover': { borderColor: TEAL, color: TEAL } } } as any,
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' as const,
    letterSpacing: '0.07em' },
  clearBtn: { padding: '7px 14px', background: 'none', border: '1px solid #E1E1E1', borderRadius: 6,
    fontSize: 12, fontWeight: 600, color: '#747474', cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { borderColor: RED, color: RED } } } as any,
  tabBar: { display: 'flex', borderBottom: '2px solid #E1E1E1', marginTop: 16, marginBottom: 16, gap: 0 },
  tabBtn: { padding: '10px 20px', fontSize: 14, fontWeight: 600, background: 'none', border: 'none',
    cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2 },
  kpiRow: { display: 'grid', gap: 14, marginBottom: 16,
    gridTemplateColumns: 'repeat(6, 1fr)',
    '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: 'repeat(2, 1fr)' } } as any,
  kpiRow4: { display: 'grid', gap: 14, marginBottom: 16, gridTemplateColumns: 'repeat(4, 1fr)',
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' } } as any,
  kpiCard: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '16px 18px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  kpiLabel: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', marginBottom: 6 },
  kpiValue: { fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: '1',
    fontVariantNumeric: 'tabular-nums' as const },
  kpiSub: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  threeCol: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16,
    '@media (max-width: 1000px)': { gridTemplateColumns: '1fr' } } as any,
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } } as any,
  card: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '16px 18px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 1 },
  cardSub: { fontSize: 12, color: '#9CA3AF', marginBottom: 12 },
  toggle: { display: 'inline-flex', border: '1px solid #E1E1E1', borderRadius: 7, overflow: 'hidden',
    background: '#fff' },
  toggleBtn: { padding: '6px 16px', fontSize: 12.5, fontWeight: 600, background: 'none', border: 'none',
    cursor: 'pointer', color: '#747474' },
  th: { textAlign: 'left' as const, padding: '8px 10px', fontWeight: 700, fontSize: 11, color: '#747474',
    background: '#F9FAFB', borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap' as const },
  td: { padding: '7px 10px', borderBottom: '1px solid #F3F4F6', color: '#232323', whiteSpace: 'nowrap' as const },
  centeredMsg: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', minHeight: 220, gap: 12, color: '#747474', fontSize: 14, textAlign: 'center' as const },
  accessBox: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: 56,
    textAlign: 'center' as const, color: '#747474' },
  accessTitle: { fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 },
});
const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #E1E1E1', borderRadius: 6,
  color: '#232323', fontSize: 12 };

function PctTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div>{p.pct != null ? `${p.pct.toFixed(2)}%` : '—'} · {fmtNum(p.count || 0)} responses</div>
    </div>
  );
}
function DeptTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.fill }}>{p.name}: {fmtNum(p.value)}</div>
      ))}
    </div>
  );
}

// Horizontal % bar chart (response on Y, pct on X) used for CSAT / Times / Resolved
function PctBars({ data, colorFn, height }: {
  data: RespRow[]; colorFn: (r: RespRow, i: number) => string; height: number;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
        <XAxis type="number" domain={[0, 'dataMax']} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
        <YAxis type="category" dataKey="response" width={150} tick={{ fontSize: 11 }}
          tickFormatter={(v) => trunc(v, 24)} />
        <Tooltip content={<PctTooltip />} />
        <Bar dataKey="pct" radius={[0, 4, 4, 0]} barSize={20}>
          {data.map((r, i) => <Cell key={i} fill={colorFn(r, i)} />)}
          <LabelList dataKey="pct" position="right" formatter={(v: any) => `${Number(v).toFixed(2)}%`}
            style={{ fontSize: 11, fill: '#484848', fontWeight: 600 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Trend arrow for period-over-period KPIs
function Trend({ current, previous, lowerIsBetter }: { current: number | null; previous: number | null; lowerIsBetter: boolean }) {
  if (current == null || previous == null) return null;
  const delta = current - previous;
  if (Math.abs(delta) < 0.05) return <span style={{ color: GREY, fontWeight: 700 }}>—</span>;
  const up = delta > 0;
  const good = lowerIsBetter ? !up : up;
  const colour = good ? GREEN : RED;
  return up
    ? <ArrowUp size={18} weight="bold" color={colour} />
    : <ArrowDown size={18} weight="bold" color={colour} />;
}

const csatColour = (r: RespRow) => BAND_COLOURS[r.response] ?? TEAL;

// ── Main component ──────────────────────────────────────────────────────────────
export default function SurveyDashboard() {
  const nav = useNavigate();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [dateRange, setDateRange] = useState('14d');

  const [directorate, setDirectorate] = useState<string[]>([]);
  const [department,  setDepartment]  = useState<string[]>([]);
  const [serviceGroup, setServiceGroup] = useState<string[]>([]);
  const [filterOpts, setFilterOpts] = useState<FilterOptions | null>(null);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);

  const [overviewView, setOverviewView] = useState<'summary' | 'department'>('summary');
  const [wcSentiment, setWcSentiment] = useState<'satisfied' | 'dissatisfied'>('satisfied');

  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [executiveData, setExecutiveData] = useState<ExecutiveData | null>(null);
  const [wordCloud, setWordCloud] = useState<WordCloudData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Access
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin'
          ? true
          : Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('customer_survey'));
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Filter options
  useEffect(() => {
    if (!hasAccess) return;
    setFilterOptsLoading(true);
    accessService.get<FilterOptions>('/survey/filter-options')
      .then(r => setFilterOpts(r.data))
      .catch(e => console.warn('[Survey] filter-options failed:', e.message))
      .finally(() => setFilterOptsLoading(false));
  }, [hasAccess]);

  // Cascade dir → dept
  useEffect(() => {
    if (!directorate.length || !filterOpts) return;
    const allowed = new Set(directorate.flatMap(d => filterOpts.dirHierarchy[d] ?? []));
    setDepartment(prev => { const next = prev.filter(x => allowed.has(x)); return next.length === prev.length ? prev : next; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorate, filterOpts]);
  const availableDepts: string[] = React.useMemo(() => {
    if (!directorate.length || !filterOpts?.dirHierarchy) return filterOpts?.allDepartments ?? [];
    return [...new Set(directorate.flatMap(d => filterOpts!.dirHierarchy[d] ?? []))].sort();
  }, [directorate, filterOpts]);

  const params = useCallback(() => {
    const { from, to } = buildDateKeys(dateRange);
    return { dateFrom: from, dateTo: to,
      ...(directorate.length ? { directorate: directorate.join(',') } : {}),
      ...(department.length ? { department: department.join(',') } : {}),
      ...(serviceGroup.length ? { serviceGroup: serviceGroup.join(',') } : {}) };
  }, [dateRange, directorate, department, serviceGroup]);

  // Clear on filter change
  useEffect(() => { setOverviewData(null); setExecutiveData(null); setWordCloud(null); },
    [dateRange, directorate, department, serviceGroup]);

  const fetchTab = useCallback(async (tab: TabKey) => {
    if (!hasAccess) return;
    setLoading(true); setError(null);
    try {
      if (tab === 'overview') {
        const r = await accessService.get<OverviewData>('/survey/overview', { params: params() });
        setOverviewData(r.data);
      } else {
        const r = await accessService.get<ExecutiveData>('/survey/executive', { params: params() });
        setExecutiveData(r.data);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load data');
    } finally { setLoading(false); }
  }, [hasAccess, params]);

  useEffect(() => { if (hasAccess) fetchTab(activeTab); }, [activeTab, fetchTab, hasAccess]);

  // Word cloud (executive tab)
  useEffect(() => {
    if (!hasAccess || activeTab !== 'executive') return;
    accessService.get<WordCloudData>('/survey/wordcloud', { params: { ...params(), sentiment: wcSentiment } })
      .then(r => setWordCloud(r.data))
      .catch(e => console.warn('[Survey] wordcloud failed:', e.message));
  }, [hasAccess, activeTab, wcSentiment, params]);

  const refreshedAt = (() => {
    const d = overviewData?.meta.generatedAt || executiveData?.meta.generatedAt;
    return d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : null;
  })();
  const hasActiveFilter = dateRange !== '14d' || directorate.length > 0 || department.length > 0 || serviceGroup.length > 0;
  const clearFilters = () => { setDateRange('14d'); setDirectorate([]); setDepartment([]); setServiceGroup([]); };
  const toggle = (set: React.Dispatch<React.SetStateAction<string[]>>) => (_: any, o?: IDropdownOption) => {
    if (!o) return; const v = String(o.key);
    set(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
  };

  // Access gate
  if (hasAccess === null) {
    return (
      <div className={s.page}><div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Spinner size={SpinnerSize.large} label="Checking access…" />
      </div></div>
    );
  }
  if (hasAccess === false) {
    return (
      <div className={s.page}><div className={s.container}>
        <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={12} weight="bold" /> Dashboards</button>
        <div className={s.accessBox}>
          <div className={s.accessTitle}>Access Required</div>
          <div style={{ fontSize: 14, color: '#747474', maxWidth: 440, margin: '0 auto' }}>
            The Customer Experience Survey dashboard requires governed access.
            Submit an access request to your administrator to get started.
          </div>
        </div>
      </div></div>
    );
  }

  // Reusable department stacked-bar card
  const deptCard = (rows: DeptRow[]) => (
    <div className={s.card}>
      <div className={s.cardTitle}>Satisfaction Breakdown by Department</div>
      <div className={s.cardSub}>Satisfied vs dissatisfied responses · from the linked request</div>
      {rows.length === 0 ? (
        <div className={s.centeredMsg} style={{ minHeight: 140 }}>
          <Info size={20} color="#9CA3AF" />
          <span>Department breakdown appears once surveys are linked to requests in the warehouse.</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 34)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
            <YAxis type="category" dataKey="department" width={200} tick={{ fontSize: 11 }} tickFormatter={(v) => trunc(v, 28)} />
            <Tooltip content={<DeptTooltip />} />
            <Bar dataKey="satisfied" name="Satisfied" stackId="a" fill={SAT_GREEN} radius={[0, 0, 0, 0]} barSize={18} />
            <Bar dataKey="dissatisfied" name="Dissatisfied" stackId="a" fill={DISSAT_RED} radius={[0, 4, 4, 0]} barSize={18} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.container}>
        <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={12} weight="bold" /> Dashboards</button>

        {/* Header */}
        <div className={s.headerRow}>
          <div>
            <div className={s.pageTitle}>
              <span style={{ width: 36, height: 36, borderRadius: 8, background: TEAL, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Smiley size={18} color="#fff" weight="bold" />
              </span>
              Customer Experience Survey
            </div>
            <div className={s.pageSub}>
              {activeTab === 'overview'
                ? 'A summary of customer satisfaction, contact frequency, and request resolution from post-service surveys'
                : 'Executive view — period-over-period performance and verbatim themes'}
              {refreshedAt ? ` · Updated ${refreshedAt}` : ''}
            </div>
          </div>
          <button className={s.refreshBtn} disabled={loading}
            onClick={() => { if (activeTab === 'overview') setOverviewData(null); else setExecutiveData(null); fetchTab(activeTab); }}>
            <ArrowsClockwise size={14} weight="bold" style={loading ? { animation: `${spinAnim} 1s linear infinite` } : {}} />
            Refresh
          </button>
        </div>

        {error && <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>{error}</MessageBar>}

        {/* Filter bar */}
        <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8, padding: '14px 20px',
          display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Directorate</span>
            <Dropdown multiSelect placeholder="All directorates"
              options={(filterOpts?.directorates ?? []).map(d => ({ key: d, text: d }))}
              selectedKeys={directorate} onChange={toggle(setDirectorate)}
              disabled={filterOptsLoading || !filterOpts?.directorates.length}
              styles={{ root: { minWidth: 175 }, dropdown: { fontSize: 13 }, callout: { minWidth: 280 } }} />
          </div>
          <div className={s.filterGroup}>
            <span className={s.filterLabel} style={{ color: directorate.length ? '#9CA3AF' : '#D1D5DB' }}>Department</span>
            <Dropdown multiSelect placeholder="All departments"
              options={availableDepts.map(d => ({ key: d, text: d }))}
              selectedKeys={department} onChange={toggle(setDepartment)}
              disabled={filterOptsLoading || !availableDepts.length}
              styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 300 } }} />
          </div>
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px' }} />
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Service</span>
            <Dropdown multiSelect placeholder="All services"
              options={(filterOpts?.serviceGroups ?? []).map(g => ({ key: g, text: g }))}
              selectedKeys={serviceGroup} onChange={toggle(setServiceGroup)}
              disabled={filterOptsLoading || !filterOpts?.serviceGroups.length}
              styles={{ root: { minWidth: 175 }, dropdown: { fontSize: 13 }, callout: { minWidth: 300 } }} />
          </div>
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px' }} />
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Survey response date</span>
            <Dropdown options={DATE_RANGE_OPTIONS} selectedKey={dateRange}
              onChange={(_, o) => o && setDateRange(String(o.key))}
              styles={{ root: { minWidth: 150 }, dropdown: { fontSize: 13 } }} />
          </div>
          {hasActiveFilter && <button className={s.clearBtn} onClick={clearFilters}>Reset all filters</button>}
        </div>

        {/* Tabs */}
        <div className={s.tabBar}>
          {TABS.map(t => (
            <button key={t.key} className={s.tabBtn} onClick={() => setActiveTab(t.key)}
              style={{ color: activeTab === t.key ? TEAL : '#747474',
                borderBottom: activeTab === t.key ? `2px solid ${TEAL}` : '2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className={s.centeredMsg}><Spinner size={SpinnerSize.large} /><span>Loading {TABS.find(t => t.key === activeTab)?.label}…</span></div>}

        {/* ═══════════ OVERVIEW ═══════════ */}
        {activeTab === 'overview' && !loading && overviewData && (() => {
          const { kpis, csatDistribution, timesContacted, requestResolved, byDepartment, detail } = overviewData;
          const csatTotal = csatDistribution.reduce((a, b) => a + b.count, 0);
          return (
            <>
              <div className={s.kpiRow}>
                <div className={s.kpiCard}><div className={s.kpiLabel}>Surveys Completed</div>
                  <div className={s.kpiValue} style={{ color: TEAL }}>{fmtNum(kpis.surveysCompleted)}</div></div>
                <div className={s.kpiCard}><div className={s.kpiLabel}>Total Requests</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.totalRequests)}</div></div>
                <div className={s.kpiCard}><div className={s.kpiLabel}>Completion Rate</div>
                  <div className={s.kpiValue}>{kpis.completionRate != null ? `${kpis.completionRate.toFixed(1)}%` : '—'}</div></div>
                <div className={s.kpiCard}><div className={s.kpiLabel}>Satisfied</div>
                  <div className={s.kpiValue} style={{ color: GREEN }}>{fmtNum(kpis.satisfied)}</div></div>
                <div className={s.kpiCard}><div className={s.kpiLabel}>Dissatisfied</div>
                  <div className={s.kpiValue} style={{ color: RED }}>{fmtNum(kpis.dissatisfied)}</div></div>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${TEAL}` }}><div className={s.kpiLabel}>CSAT</div>
                  <div className={s.kpiValue} style={{ color: TEAL }}>{kpis.csatPct != null ? `${kpis.csatPct.toFixed(2)}%` : '—'}</div></div>
              </div>

              {/* Summary / Department toggle */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <div className={s.toggle}>
                  {(['summary', 'department'] as const).map(v => (
                    <button key={v} className={s.toggleBtn} onClick={() => setOverviewView(v)}
                      style={overviewView === v ? { background: TEAL, color: '#fff' } : {}}>
                      {v === 'summary' ? 'Summary View' : 'Department View'}
                    </button>
                  ))}
                </div>
              </div>

              {overviewView === 'summary' ? (
                <div className={s.threeCol}>
                  <div className={s.card}>
                    <div className={s.cardTitle}>Overall Customer Satisfaction Score</div>
                    <div className={s.cardSub}>Total Responses: {fmtNum(csatTotal)}</div>
                    <PctBars data={csatDistribution} colorFn={csatColour} height={230} />
                  </div>
                  <div className={s.card}>
                    <div className={s.cardTitle}>Times Contacted Council</div>
                    <div className={s.cardSub}>Total Responses: {fmtNum(timesContacted.reduce((a, b) => a + b.count, 0))}</div>
                    <PctBars data={timesContacted} colorFn={() => TEAL} height={230} />
                  </div>
                  <div className={s.card}>
                    <div className={s.cardTitle}>Request Resolved</div>
                    <div className={s.cardSub}>Total Responses: {fmtNum(requestResolved.reduce((a, b) => a + b.count, 0))}</div>
                    <PctBars data={requestResolved} colorFn={() => TEAL} height={230} />
                  </div>
                </div>
              ) : deptCard(byDepartment)}

              {/* Detail table */}
              <div className={s.card}>
                <div className={s.cardTitle}>Survey Responses</div>
                <div className={s.cardSub}>{fmtNum(detail.length)} most recent responses{detail.length >= 500 ? ' (capped at 500)' : ''}</div>
                {detail.length === 0 ? (
                  <div className={s.centeredMsg} style={{ minHeight: 140 }}>
                    <Info size={20} color="#9CA3AF" />
                    <span>Response detail appears once surveys are linked to requests in the warehouse.</span>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr>
                        <th className={s.th}>CRM Link</th><th className={s.th}>Reference #</th>
                        <th className={s.th}>Request Close Date</th><th className={s.th}>Survey Response Date</th>
                        <th className={s.th}>Directorate</th><th className={s.th}>Department</th>
                        <th className={s.th}>Service</th><th className={s.th}>Service Type</th>
                      </tr></thead>
                      <tbody>
                        {detail.map((r, i) => (
                          <tr key={i}>
                            <td className={s.td}>
                              {r.incidentId ? (
                                <a href={crmIncidentUrl(r.incidentId)} target="_blank" rel="noreferrer"
                                  title={`Open incident ${r.incidentId} in CRM`} style={{ color: TEAL, display: 'inline-flex' }}>
                                  <ArrowSquareOut size={15} weight="bold" />
                                </a>
                              ) : <ArrowSquareOut size={15} color="#C4C4C4" />}
                            </td>
                            <td className={s.td} style={{ fontWeight: 600 }}>{r.reference}</td>
                            <td className={s.td}>{fmtIso(r.requestCloseDate)}</td>
                            <td className={s.td}>{fmtDateKey(r.surveyDateKey)}</td>
                            <td className={s.td}>{r.directorate}</td>
                            <td className={s.td}>{r.department}</td>
                            <td className={s.td}>{r.service}</td>
                            <td className={s.td}>{trunc(r.serviceTypeL2 || r.serviceTypeL1, 28)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {/* ═══════════ EXECUTIVE SUMMARY ═══════════ */}
        {activeTab === 'executive' && !loading && executiveData && (() => {
          const { kpis, csatDistribution, timesContacted, requestResolved, byDepartment } = executiveData;
          const cur = kpis.current, prev = kpis.previous;
          const csatTotal = csatDistribution.reduce((a, b) => a + b.count, 0);
          const kpi = (label: string, c: number | null, pv: number | null, lowerIsBetter: boolean) => (
            <div className={s.kpiCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className={s.kpiLabel}>{label}</div>
                <Trend current={c} previous={pv} lowerIsBetter={lowerIsBetter} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 4 }}>
                <div><div className={s.kpiValue} style={{ fontSize: 26 }}>{c != null ? `${Math.round(c)}%` : '—'}</div>
                  <div className={s.kpiSub}>Selected period</div></div>
                <div><div style={{ fontSize: 20, fontWeight: 700, color: '#9CA3AF' }}>{pv != null ? `${Math.round(pv)}%` : '—'}</div>
                  <div className={s.kpiSub}>Previous</div></div>
              </div>
            </div>
          );
          return (
            <>
              <div className={s.kpiRow4}>
                {kpi('Overall Satisfied', cur.satisfiedPct, prev.satisfiedPct, false)}
                {kpi('Overall Dissatisfied', cur.dissatisfiedPct, prev.dissatisfiedPct, true)}
                {kpi('Contacted Council 3+ times', cur.contact3PlusPct, prev.contact3PlusPct, true)}
                {kpi('Request Unresolved', cur.unresolvedPct, prev.unresolvedPct, true)}
              </div>

              <div className={s.twoCol}>
                <div className={s.card}>
                  <div className={s.cardTitle}>Satisfaction Score Breakdown</div>
                  <div className={s.cardSub}>Total Responses: {fmtNum(csatTotal)}</div>
                  <PctBars data={csatDistribution} colorFn={csatColour} height={230} />
                </div>
                {deptCard(byDepartment)}
              </div>

              <div className={s.twoCol}>
                <div className={s.card}>
                  <div className={s.cardTitle}>Times Contacted Council</div>
                  <div className={s.cardSub}>Total Responses: {fmtNum(timesContacted.reduce((a, b) => a + b.count, 0))}</div>
                  <PctBars data={timesContacted} colorFn={() => TEAL} height={200} />
                </div>
                <div className={s.card}>
                  <div className={s.cardTitle}>Request Resolved</div>
                  <div className={s.cardSub}>Total Responses: {fmtNum(requestResolved.reduce((a, b) => a + b.count, 0))}</div>
                  <PctBars data={requestResolved} colorFn={() => TEAL} height={200} />
                </div>
              </div>

              {/* Verbatim word cloud */}
              <div className={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                  <div>
                    <div className={s.cardTitle}>Verbatim Text: {wcSentiment === 'satisfied' ? 'Satisfied' : 'Dissatisfied'} Responses</div>
                    <div className={s.cardSub}>
                      Most recent {wordCloud ? fmtNum(wordCloud.sampled) : '—'} comments from customers rating overall satisfaction as
                      {wcSentiment === 'satisfied' ? ' ‘Satisfied’ or ‘Very Satisfied’' : ' ‘Dissatisfied’ or ‘Very Dissatisfied’'}
                    </div>
                  </div>
                  <div className={s.toggle}>
                    {(['satisfied', 'dissatisfied'] as const).map(v => (
                      <button key={v} className={s.toggleBtn} onClick={() => setWcSentiment(v)}
                        style={wcSentiment === v ? { background: v === 'satisfied' ? SAT_GREEN : DISSAT_RED, color: '#fff' } : {}}>
                        {v === 'satisfied' ? 'Satisfied' : 'Dissatisfied'}
                      </button>
                    ))}
                  </div>
                </div>
                <WordCloud data={wordCloud} sentiment={wcSentiment} />
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ── Word cloud (native) ─────────────────────────────────────────────────────────
function WordCloud({ data, sentiment }: { data: WordCloudData | null; sentiment: 'satisfied' | 'dissatisfied' }) {
  if (!data) return <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={SpinnerSize.medium} label="Building word cloud…" /></div>;
  if (!data.words.length) return <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13 }}>No verbatim text for this selection yet.</div>;
  const max = data.words[0].count, min = data.words[data.words.length - 1].count;
  const span = Math.max(1, max - min);
  const palette = sentiment === 'satisfied'
    ? ['#15803d', '#16a34a', '#0D9488', '#0891B2', '#22c55e', '#65a30d']
    : ['#b91c1c', '#dc2626', '#ea580c', '#d97706', '#9333ea', '#be123c'];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
      gap: '6px 14px', padding: '18px 8px', minHeight: 220, lineHeight: 1.1 }}>
      {data.words.map((w, i) => {
        const size = 13 + Math.round((28 * (w.count - min)) / span);
        return (
          <span key={w.word} title={`${w.word}: ${w.count}`}
            style={{ fontSize: size, fontWeight: size > 26 ? 800 : size > 18 ? 700 : 600,
              color: palette[i % palette.length], letterSpacing: '-0.01em' }}>
            {w.word}
          </span>
        );
      })}
    </div>
  );
}
